import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Alert,
  useTheme,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { NewHire } from '../../services/hireService';
import { supabase } from '../../config/supabase';
import TimeSinceCell from '../common/TimeSinceCell';

interface MassArchiveDialogProps {
  open: boolean;
  onClose: () => void;
  onArchiveComplete: () => void; // Callback to refresh the list
  hires: NewHire[];
}

const MassArchiveDialog: React.FC<MassArchiveDialogProps> = ({
  open,
  onClose,
  onArchiveComplete,
  hires,
}) => {
  const [selectedHireIds, setSelectedHireIds] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [archiving, setArchiving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();

  // Filter hires based on search term
  const filteredHires = useMemo(() => {
    let filtered = hires.filter(hire => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        hire.fullName?.toLowerCase().includes(searchLower) ||
        hire.email?.toLowerCase().includes(searchLower) ||
        hire.mobileNumber?.toLowerCase().includes(searchLower) ||
        hire.state?.toLowerCase().includes(searchLower)
      );
    });

    // Sort by time since last update (oldest first)
    // Use update_timestamp if available, otherwise fallback to created_at
    filtered.sort((a, b) => {
      const aTime = a.update_timestamp 
        ? new Date(a.update_timestamp).getTime() 
        : (a.created_at ? new Date(a.created_at).getTime() : 0);
      const bTime = b.update_timestamp 
        ? new Date(b.update_timestamp).getTime() 
        : (b.created_at ? new Date(b.created_at).getTime() : 0);
      return aTime - bTime; // Oldest first
    });

    return filtered;
  }, [hires, searchTerm]);

  const handleToggleAll = () => {
    if (selectedHireIds.size === filteredHires.length) {
      // Deselect all
      setSelectedHireIds(new Set());
    } else {
      // Select all filtered hires
      setSelectedHireIds(new Set(filteredHires.map(hire => hire.id)));
    }
  };

  const handleToggleHire = (hireId: string) => {
    const newSelected = new Set(selectedHireIds);
    if (newSelected.has(hireId)) {
      newSelected.delete(hireId);
    } else {
      newSelected.add(hireId);
    }
    setSelectedHireIds(newSelected);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleConfirm = async () => {
    if (selectedHireIds.size === 0) {
      setError('Please select at least one hire to archive.');
      return;
    }

    try {
      setArchiving(true);
      setError(null);
      
      // Get hires to archive
      const hiresToArchive = hires.filter(hire => selectedHireIds.has(hire.id));
      
      // Archive each hire
      for (const hire of hiresToArchive) {
        // Insert into new_hire_archive table with current timestamp
        const { error: insertError } = await supabase
          .from('new_hire_archive')
          .insert([{
            ...hire,
            created_at: new Date().toISOString() // Set archive timestamp
          }]);

        if (insertError) {
          console.error(`Error archiving hire ${hire.fullName}:`, insertError);
          continue; // Continue with next hire even if one fails
        }

        // Remove from new_hire table
        const { error: deleteError } = await supabase
          .from('new_hire')
          .delete()
          .eq('id', hire.id);

        if (deleteError) {
          console.error(`Error deleting hire ${hire.fullName} from new_hire:`, deleteError);
          continue;
        }

        // Remove from profiles table (new_hire.id = profiles.id)
        const { error: profileDeleteError } = await supabase
          .from('profiles')
          .delete()
          .eq('id', hire.id);

        if (profileDeleteError) {
          console.warn(`Error deleting profile for ${hire.fullName} (may not exist):`, profileDeleteError);
          // Don't throw error - profile might not exist, continue with archive process
        }

        console.log(`Successfully archived hire: ${hire.fullName}`);
      }

      // Reset state on success
      setSelectedHireIds(new Set());
      setSearchTerm('');
      onClose();
      
      // Call callback to refresh the list
      if (onArchiveComplete) {
        onArchiveComplete();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to archive hires. Please try again.');
    } finally {
      setArchiving(false);
    }
  };

  const handleClose = () => {
    if (!archiving) {
      setSelectedHireIds(new Set());
      setSearchTerm('');
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle
        sx={{
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontWeight: 600,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <WarningIcon />
          <Typography variant="h6">Mass Archive Hires</Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
          Select multiple hires to archive. This action will move the selected records to the archive table and remove them from the active hires list.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Search Box */}
        <TextField
          fullWidth
          placeholder="Search hires by name, email, phone, or state..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          size="small"
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton onClick={handleClearSearch} size="small">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Selection Controls */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {selectedHireIds.size} of {filteredHires.length} selected
          </Typography>
          <Button
            onClick={handleToggleAll}
            size="small"
            disabled={filteredHires.length === 0}
            variant="outlined"
          >
            {selectedHireIds.size === filteredHires.length ? 'Deselect All' : 'Select All'}
          </Button>
        </Box>

        {/* Hires Table */}
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            maxHeight: 400,
            overflowY: 'auto',
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: 50 }}>
                  <Checkbox
                    indeterminate={selectedHireIds.size > 0 && selectedHireIds.size < filteredHires.length}
                    checked={filteredHires.length > 0 && selectedHireIds.size === filteredHires.length}
                    onChange={handleToggleAll}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>State</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Time Since Update</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredHires.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      {searchTerm ? 'No hires found matching your search.' : 'No hires available to archive.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredHires.map((hire) => (
                  <TableRow
                    key={hire.id}
                    hover
                    sx={{
                      backgroundColor: selectedHireIds.has(hire.id)
                        ? (theme.palette.mode === 'dark'
                            ? 'rgba(102, 126, 234, 0.1)'
                            : 'rgba(102, 126, 234, 0.05)')
                        : 'transparent',
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedHireIds.has(hire.id)}
                        onChange={() => handleToggleHire(hire.id)}
                      />
                    </TableCell>
                    <TableCell>{hire.fullName}</TableCell>
                    <TableCell>{hire.email}</TableCell>
                    <TableCell>{hire.mobileNumber}</TableCell>
                    <TableCell>{hire.state}</TableCell>
                    <TableCell>
                      <TimeSinceCell
                        dateString={(hire.update_timestamp || hire.created_at) as string}
                        label={hire.update_timestamp ? "updated" : "created"}
                        displayDate={(hire.update_timestamp || hire.created_at) ? new Date(hire.update_timestamp || hire.created_at).toLocaleString('en-US', {
                          month: '2-digit',
                          day: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false
                        }) : undefined}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {archiving && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
              Archiving {selectedHireIds.size} hire{selectedHireIds.size !== 1 ? 's' : ''}...
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={archiving}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={archiving || selectedHireIds.size === 0}
          sx={{
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
                : 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
            },
            '&:disabled': {
              background: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.1)',
            },
          }}
        >
          {archiving
            ? `Archiving ${selectedHireIds.size}...`
            : `Archive ${selectedHireIds.size} Hire${selectedHireIds.size !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MassArchiveDialog;

